import { Widget } from './widget';
import { ManyToManyWidget } from './manytomanywidget';
import { WidgetException } from './widgetexception';
import IWidget from './iwidget';
import {WidgetToggler} from "./widgetToggler";

/**
 * todo describe WidgetIndex (usage, use, where it's used)
 */
export class WidgetIndex {
    public widgets: Array<IWidget> = [];
    public toggler: WidgetToggler;

    constructor() {
        this.toggler = new WidgetToggler(this);
    }

    /**
     *
     * @param {Widget} widget
     */
    addWidget(widget: IWidget): void {
        this.widgets.push(widget);

        // listen to widget changes
        widget.listen('change', async () => {
            await WidgetIndex.validateSingle(widget);
        });
    }

    /**
     *
     */
    clear(): void {
        this.widgets = [];
    }

    /**
     * gets an array of objects: [{name: widget_name, value: widget_value },...]
     * @param {array} widgets
     * @return {Promise<Map>}
     */
    async getValues(widgets: Array<IWidget>): Promise<Map<string, any>> {
        const promises = widgets.map(async (w) => w.getValue()
            .then((x) => new Map([
                ['name', w.getName()],
                ['value', x],
            ])));

        try {
            const result = await Promise.all(promises);
            const values = new Map();

            result.forEach((x: Map<string, any>) => {
                values.set(x.get('name'), x.get('value'));
            });

            return values;
        } catch (e) {
            console.log(e);
            return Promise.reject(new Error('couldn\'t get all values: '));
        }
    }

    /**
     * gets an array of objects: [{name: widget_name, value: mid_values },...]
     * @param {array} widgets
     * @return {Promise<Map>}
     */
    async getMidValues(widgets: Array<ManyToManyWidget>): Promise<Map<string, Array<any>>> {
        try {
            const promises = widgets.map(async (w) => w.getMidValues()
                .then((x) => {
                    const map = new Map();
                    map.set('name', w.getName());
                    map.set('value', x);
                    return map;
                }));

            const result = await Promise.all(promises);
            const values = new Map();

            result.forEach((x: Map<string, Array<any>>) => {
                values.set(x.get('name'), x.get('value'));
            });

            return values;
        } catch (e) {
            console.log(e);
            return Promise.reject(new Error('couldn\'t get all values'));
        }
    }

    /**
     * gets an array of objects: [{name: widget_name, value: remote_values },...]
     * @param {array} widgets
     * @return {Promise<Map>}
     */
    async getRemoteValues(widgets: Array<ManyToManyWidget>): Promise<Map<string, Array<any>>> {
        try {
            const promises = widgets.map(async (w) => w.getRemoteValues()
                .then((x) => {
                    const map = new Map();
                    map.set('name', w.getName());
                    map.set('value', x);
                    return map;
                }));

            const result = await Promise.all(promises);
            const values = new Map();

            result.forEach((x: Map<string, Array<any>>) => {
                values.set(x.get('name'), x.get('value'));
            });

            return values;
        } catch (e) {
            console.log(e);
            return Promise.reject(new Error('couldn\'t get all values: '));
        }
    }

    /**
     * validates a single widget
     * @param w {IWidget}
     * @return {Promise<void>}
     */
    static async validateSingle(w: IWidget): Promise<void> {
        await w.validate();
    }

    /**
     * validates all widgets
     * @return {Promise<boolean>}
     */
    async validate(): Promise<boolean> {
        const formTabs = document.querySelectorAll('.tabs li.form-tab');
        const classesToRemove = ['is-danger', 'is-warning', 'is-info', 'is-success'];

        const errors: WidgetException[] = [];
        const promises = this.widgets.map(async (w) => w.validate());
        const validationResults = await Promise.allSettled(promises);

        // unmark form tabs
        formTabs.forEach((t) => {
            t.classList.remove(...classesToRemove);
        });

        validationResults.forEach((v) => {
            if (v.status !== 'fulfilled') {
                const error: WidgetException = v.reason;
                errors.push(error);

                // mark form tab (e.g. FR or "general")
                if (error.widget.lang != '') {
                    formTabs.forEach((t) => {
                        if ((t as HTMLElement).dataset.lang == error.widget.lang) {
                            t.classList.add(`is-${error.severity}`);
                        }
                    })
                } else if (formTabs.length > 0) { // mark general tab
                    formTabs[0].classList.add(`is-${error.severity}`);
                }
            }
        });

        console.log('validation failures:', errors);
        return errors.length === 0;
    }

    /**
     * runs the preupload task on all widgets
     * @return {Promise<boolean>}
     */
    async runPreUploadTasks(): Promise<boolean> {
        const promises = this.widgets.map(async (w) => w.preUploadTask());

        const failures = (await Promise.all(promises)).filter((x) => x === false);
        console.log('preupload failures:', failures);
        return failures.length === 0;
    }

    /**
     * find a widget with name and lang
     * @param name
     * @param lang
     */
    findWidget(name: string, lang?: string): IWidget|false {
        const foundWidget = this.widgets.find((w) => w.getName() === name && w.lang == lang);
        return typeof foundWidget === 'undefined' ? false : foundWidget;
    }

    /**
     * use the WidgetToggler to observe widgets (to toggle visibility)
     */
    observe(): void {
        this.widgets.forEach(w => this.toggler.observe(w));
        this.toggler.process();
    }
}
